import { GraphNode } from '../graph-node.js';
import { instantiateGraph } from '../instantiate-graph.js';
import { EventNode } from './event-node.js';
import { describeNodeData, traceGraph, traceGraphWarning } from '../debug.js';
import { reportGraphError } from '../errors.js';
const MAX_FUNCTION_CALL_DEPTH = 64;
let activeCallDepth = 0;
/** Value wires into Return carry results only - without an exec wire it never fires. */
function hasExecPathIntoReturn(ret) {
    for (const connections of ret.inConnections.values()) {
        const isExecWire = connections.some((connection) => connection.connectedNode.isExecOutPort(connection.outPortKey));
        if (isExecWire)
            return true;
    }
    return false;
}
/**
 * Invokes a custom function subgraph.
 *
 * - With a FunctionReturnNode: waits for the return, copies the return values, then
 *   continues the caller on the exec out-port matching the Return pin that fired.
 *   Later returns within the same call (latent paths such as "onFinished") continue
 *   the caller on their own exec out-port.
 * - Without return (fire-and-forget): starts the body + event listeners, then continues
 *   the caller immediately. This matches UE5 functions that only drive side effects.
 */
export class CallFunctionNode extends GraphNode {
    nodeData;
    subGraphs;
    bodyNodesOfCurrentCall = [];
    constructor(id, data, type, nodeData, subGraphs) {
        super(id, data, type);
        this.nodeData = nodeData;
        this.subGraphs = subGraphs;
    }
    async run() {
        this.getDataFromInPorts();
        if (activeCallDepth >= MAX_FUNCTION_CALL_DEPTH) {
            this.reportConfigurationProblem(`call depth limit (${MAX_FUNCTION_CALL_DEPTH}) exceeded - the function probably calls itself`);
            this.triggerOutPort('exec');
            return;
        }
        const functionId = typeof this.data.functionId === 'string' ? this.data.functionId : '';
        traceGraph(`callFunction (${this.id}) invoked`, {
            functionId,
            availableFunctionIds: Object.keys(this.subGraphs),
            inputs: describeNodeData(this.data),
        });
        const subgraph = this.subGraphs[functionId];
        if (!subgraph) {
            this.reportConfigurationProblem(`unknown functionId "${functionId}" - available: [${Object.keys(this.subGraphs).join(', ')}]`);
            this.triggerOutPort('exec');
            return;
        }
        activeCallDepth += 1;
        try {
            await this.invokeSubgraph(subgraph, functionId);
        }
        finally {
            activeCallDepth -= 1;
        }
    }
    async invokeSubgraph(subgraph, functionId) {
        const locals = this.nodeData.localVariablesByFunction?.[functionId] ?? {};
        const parentStore = this.nodeData.variableStore ?? null;
        const callStore = parentStore ? parentStore.createLocalChild(locals) : null;
        const callNodeData = {
            ...this.nodeData,
            variableStore: callStore ?? this.nodeData.variableStore
        };
        const instance = instantiateGraph(subgraph, callNodeData);
        traceGraph(`callFunction (${this.id}) body instantiated`, {
            functionId,
            nodeTypes: instance.nodes.map((node) => node.type),
            connectionCount: subgraph.connections.length,
        });
        const entry = instance.nodes.find((node) => node.type === 'functionEntry');
        const ret = instance.nodes.find((node) => node.type === 'functionReturn');
        if (!entry) {
            this.reportConfigurationProblem(`function "${functionId}" is missing its Entry node`);
            this.triggerOutPort('exec');
            return;
        }
        this.injectCallInputsIntoEntry(entry);
        this.registerSubgraphEventNodes(instance.nodes);
        const entryExecTargets = entry.outConnections.get('exec') ?? [];
        if (entryExecTargets.length === 0) {
            // Value wires alone never start the body — Entry.exec must reach the first action.
            this.reportConfigurationProblem(`function "${functionId}" has an unconnected Entry.exec - wire Function Entry ` +
                `to the first node (e.g. textToSpeech), otherwise the body never runs`);
            this.triggerOutPort('exec');
            return;
        }
        if (!ret) {
            // Side-effect-only function: kick off body and continue caller without waiting.
            traceGraph(`callFunction (${this.id}) has no Return node - fire and forget`);
            entry.run();
            this.triggerOutPort('exec');
            return;
        }
        if (!hasExecPathIntoReturn(ret)) {
            // Waiting for a Return that nothing can reach would stall the caller forever.
            this.reportConfigurationProblem(`function "${functionId}" has a Return node without an incoming exec wire - ` +
                `wire the last node of the body to "Return", otherwise the call cannot return`);
            entry.run();
            this.triggerOutPort('exec');
            return;
        }
        traceGraph(`callFunction (${this.id}) waiting for Return`);
        const firstReturn = await this.awaitFirstReturn(entry, ret);
        traceGraph(`callFunction (${this.id}) returned via "${firstReturn.execPortKey}"`, describeNodeData(firstReturn.outputs));
        this.continueCaller(firstReturn);
    }
    awaitFirstReturn(entry, ret) {
        return new Promise((resolve) => {
            let hasReturned = false;
            ret.setReturnResolver((signal) => {
                if (hasReturned) {
                    this.continueFromLatentReturn(signal);
                    return;
                }
                hasReturned = true;
                resolve(signal);
            });
            entry.run();
        });
    }
    /**
     * A body can reach Return more than once per call - e.g. immediately over the
     * synchronous `exec` chain and again later from an event or an action's
     * "onFinished". Every additional return continues the caller on its own exec
     * path instead of being dropped.
     */
    continueFromLatentReturn(signal) {
        traceGraph(`callFunction (${this.id}) latent return via "${signal.execPortKey}"`, describeNodeData(signal.outputs));
        this.continueCaller(signal);
    }
    continueCaller(signal) {
        const returnedPortKeys = Object.keys(signal.outputs);
        for (const key of returnedPortKeys) {
            this.data[key] = signal.outputs[key];
        }
        // Must happen before the exec chain continues: an event node bound to a returned
        // value has to hold that value before the caller can trigger the matching event.
        this.notifyValueConsumersOfChangedOutputs(returnedPortKeys);
        this.triggerOutPort(signal.execPortKey);
    }
    /**
     * Reading an output before the first call is expected - event nodes downstream
     * subscribe while the graph boots. `continueCaller` refreshes them once the call
     * returns, so a missing value is not an error here.
     */
    getOutPortData(outPortKey) {
        const value = this.data[outPortKey];
        if (value === undefined) {
            traceGraph(`callFunction (${this.id}) has no value for "${outPortKey}" yet - the function has not returned`);
        }
        return value;
    }
    reportConfigurationProblem(summary) {
        reportGraphError({
            kind: 'configuration',
            nodeType: this.type,
            nodeId: this.id,
            summary,
        });
    }
    injectCallInputsIntoEntry(entry) {
        const injected = [];
        for (const [key, value] of Object.entries(this.data)) {
            if (key === 'exec' || key === 'functionId')
                continue;
            entry.data[key] = value;
            injected.push(key);
        }
        traceGraph(`callFunction (${this.id}) injected parameters into Entry`, {
            injected,
            entryOutPortsWired: [...entry.outConnections.keys()],
            entryData: describeNodeData(entry.data),
        });
        const wiredValuePorts = [...entry.outConnections.keys()].filter((key) => key !== 'exec');
        const missing = wiredValuePorts.filter((key) => entry.data[key] === undefined);
        if (missing.length > 0) {
            traceGraphWarning(`callFunction (${this.id}) Entry has wired ports without a value: ${missing.join(', ')} — ` +
                `parameter keys on the Call node and the Entry node do not match`);
        }
    }
    /**
     * Event nodes inside a function body (e.g. onPlayerPlayAndAudioActive) must register
     * their listeners when the call starts — same as RuntimeEngine.init does for main.
     */
    registerSubgraphEventNodes(nodes) {
        this.disposeBodyOfPreviousCall();
        this.bodyNodesOfCurrentCall = nodes;
        for (const node of nodes) {
            if (node instanceof EventNode) {
                node.run();
            }
        }
    }
    /**
     * Every call instantiates its own body, so without this the previous call's event
     * nodes stay subscribed and each event fires once per call ever made from here.
     * A new call replaces the previous one at this call site.
     */
    disposeBodyOfPreviousCall() {
        const previousBodyNodes = this.bodyNodesOfCurrentCall;
        this.bodyNodesOfCurrentCall = [];
        if (previousBodyNodes.length === 0)
            return;
        traceGraph(`callFunction (${this.id}) disposing the body of its previous call`);
        for (const node of previousBodyNodes)
            node.dispose();
    }
    /** Disposal has to reach nested calls: their bodies hold subscriptions too. */
    dispose() {
        this.disposeBodyOfPreviousCall();
    }
}
